import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Typography
} from '@mui/material';
import { stringRequiredValidationScheme } from '@utils/validation';
import { Form, Formik } from 'formik';
import React from 'react';
import { IFlowConditions, ITaskConditions } from 'types/flows';
import { array, boolean, lazy, mixed, number, object, string } from 'yup';
import { FormikTextField } from '../../../Inputs';
import { conditionKeys, OperatorTypes } from './AtomicCondition/constants';
import { getResetValue } from './AtomicConditionsReset';
import {
	elseStepCondition,
	ELSE_INTERACTION_ID,
	FORM_KEYS,
	getElseLabel,
	newCondition
} from './helpers';
import TaskConditionsList from './TaskConditionsList';
import { IOnConditionChange, IValues } from './types';

interface ITaskConditionsDialogProps {
	taskName?: string;
	taskConditions?: ITaskConditions;
	conditions: IFlowConditions;
	onDone: (newName: string, newConditions: ITaskConditions) => void;
	onCancel: () => void;
}

const TaskConditionsDialog = ({
	taskName,
	conditions,
	taskConditions,
	onDone,
	onCancel
}: ITaskConditionsDialogProps) => {
	const required = intl.tr('validations.field.required');

	const validationScheme = object({
		taskName: stringRequiredValidationScheme(),
		taskConditions: array().of(
			object().shape({
				name: stringRequiredValidationScheme(),
				logicalOr: boolean(),
				atomicConditions: lazy((conditionValue) =>
					array().of(
						object().shape({
							target: object().shape({
								value: stringRequiredValidationScheme()
							}),
							operator: stringRequiredValidationScheme(),
							predicate: object().shape({
								value: lazy((_, { index, value }: any) => {
									const operator = conditionValue[index]?.operator;
									if (operator) {
										if (OperatorTypes.targetless.includes(operator)) {
											return mixed();
										}
										if (OperatorTypes.multiple.includes(operator)) {
											return array()
												.of(string())
												.min(1, required)
												.required(required);
										}
									}

									if (!index || !value) {
										return mixed().required(required);
									}

									const { target } = conditionValue[index];
									const { type } = conditionKeys.filter(
										({ name }) => name === target.value
									)[0];
									switch (type) {
										case 'number':
											return number().required(required);
										case 'string':
											return string().required(required);
										case 'boolean':
											return boolean().required(required);
										default:
											return mixed();
									}
								})
							})
						})
					)
				)
			})
		)
	});

	const initialValues: IValues = {
		taskName: taskName || '',
		taskConditions: (taskConditions || [newCondition()]).filter(
			(c) => c.interactionId !== ELSE_INTERACTION_ID
		),
		elseName: getElseLabel(taskConditions)
	};

	return (
		<Dialog
			data-testid="condition-dialog"
			disableRestoreFocus
			open
			onClose={onCancel}
			maxWidth="md"
			sx={{
				minHeight: '1200px',
				minWidth: '700px'
			}}
		>
			<Formik
				initialValues={initialValues}
				validationSchema={validationScheme}
				onSubmit={(newValues) => {
					const newConditions = [
						...newValues.taskConditions,
						{ ...elseStepCondition, name: newValues.elseName }
					];
					onDone(newValues.taskName, newConditions as ITaskConditions);
				}}
				validateOnMount
			>
				{({ values, setErrors, setFieldValue }) => (
					<Form noValidate>
						<DialogTitle>
							<Box
								display="flex"
								flexDirection="row"
								data-testid="condition-task-dialog-header"
							>
								<Typography variant="h6">
									{taskName || intl.tr('add.condition.task.dialog.title')}
								</Typography>
							</Box>
						</DialogTitle>
						<DialogContent sx={{ pb: 0, maxHeight: 600 }}>
							<Grid container spacing={2} pt={1}>
								<Grid item xs={12}>
									<FormikTextField
										name={FORM_KEYS.taskName}
										required
										fullWidth
										label={intl.tr('flows.builder.task.dialog.task.name')}
									/>
								</Grid>
								<Grid item xs={12}>
									<TaskConditionsList
										conditions={conditions}
										taskConditions={values.taskConditions}
										onConditionChange={({
											taskIndex,
											condIndex,
											...rest
										}: IOnConditionChange) => {
											const fieldId = `taskConditions.${taskIndex}.atomicConditions.${condIndex}.predicate.value`;
											setFieldValue(
												fieldId,
												getResetValue({
													taskIndex,
													condIndex,
													...rest,
													values
												})
											);
											setErrors({ taskConditions: undefined });
										}}
									/>
								</Grid>
							</Grid>
						</DialogContent>
						<DialogActions sx={{ pr: 2, pb: 2 }}>
							<Button
								onClick={onCancel}
								data-testid="condition-dialog-cancel-button"
							>
								{intl.tr('button.cancel')}
							</Button>
							<Button
								variant="contained"
								color="primary"
								type="submit"
								data-testid="condition-dialog-done-button"
							>
								{taskName ? intl.tr('button.done') : intl.tr('button.add')}
							</Button>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

TaskConditionsDialog.defaultProps = {
	taskName: '',
	taskConditions: undefined
};

export default TaskConditionsDialog;
